import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import isEqual from 'lodash/isEqual';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  concatMap,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';
import moment from 'moment';

import { CheckoutStatus } from '@shared/stripe/enums';
import { PartyTypes } from '@shared/party/enums';
import { CurrencyConfig, getCurrencyConfig } from '@shared/stripe/models';
import { AuthUser } from '@shared/signup/models';
import { Schedule } from '@shared/schedule/models';
import { Resource } from '@shared/resource/models';
import { Location } from '@shared/location/models';
import { UserLocationRole } from '@shared/user/enums';
import { getCancellationPolicyWindowInMilliseconds } from '@shared/location/functions';
import { Party } from '@shared/party';
import {
  ApiResponseModelWrapper,
  IParty,
  DateTimeService,
  FloorplanResources,
} from '@tablesready/common-ui';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { ConfirmationCheckboxModalService } from '@tablesready/frontend-confirmation-checkbox-modal';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { AddEditPartyComponent } from '../reservations/components';
import { AddEditPartyReactiveComponent } from '../reservations/components/add-edit-party-reactive/add-edit-party-reactive.component';
import { ScheduleService } from './schedule.service';
import { IPartyOptions, MarkPartyService } from './mark-party.service';
import { ResourcesService } from './resources.service';
import { AddEditPartyFloorplanComponent } from '../reservations/components/add-edit-party-floorplan/add-edit-party-floorplan.component';
import { PartiesUsageService } from './parties-usage.service';
import { ChatPartyService } from '@tablesready/frontend-chat-party';

type ModalInstance = AddEditPartyFloorplanComponent | AddEditPartyComponent;

@Injectable({ providedIn: 'root' })
export class ReservationService {
  schedule: Schedule[] = [];

  chatPartyService = inject(ChatPartyService);
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly modalController: FrontendModalService,
    private readonly dateTimeService: DateTimeService,
    private readonly scheduleService: ScheduleService,
    private readonly alertsService: AlertsService,
    private readonly markPartyService: MarkPartyService,
    private readonly confirmationModalService: ConfirmationCheckboxModalService,
    private readonly resourcesService: ResourcesService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly partiesUsageService: PartiesUsageService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
  ) {
    this.subscriptions.add(this.scheduleService.selectAll()
      .pipe(
        tap((schedule) => {
          this.schedule = schedule;
        }),
        catchError(() => {
          this.alertsService.addError('Please, configure business hours');

          return of(null);
        }),
      )
      .subscribe());
  }

  confirmDelete(party: IParty): Observable<unknown> {
    const { location } = this.authRestaurantService.get();
    const label = location.displayOptions.labels.reservation;
    const cancellationTime = getCancellationPolicyWindowInMilliseconds(location.bookingOptions.cancellationPolicy);

    let content = `Are you sure you want to delete ${label}?`;
    const isInsideNoCancellationWindow = Date.now() >= party.quoted_time - cancellationTime;
    const currency = getCurrencyConfig(location.integrations?.stripe?.currency || 'usd');
    const paid = party.stripeData?.status === CheckoutStatus.paid;
    const amount = `${currency.symbol}${party.stripeData?.totalAmount}`;
    if (paid) {
      if (isInsideNoCancellationWindow) {
        content = `This ${label} made a payment of ${amount} that will not be refunded because it is too close to their ${label}. Are you sure you want to delete this ${label}?`;
      } else {
        content = `This ${label} will be canceled and fully refunded. It may take a few days for the money to reach the customer’s bank account. Are you sure you wish to proceed?`;
      }
    }

    return this.confirmationModalService.confirm({
      content,
      closeBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      showCheckBox: paid && isInsideNoCancellationWindow,
      checkboxContent: `Refund the ${amount} payment. It may take a few days for the money to reach the customer’s bank account.`,
      confirmButtonActiveOnCheckboxSelected: false,
    })
      .pipe(
        take(1),
        filter(({ result }) => result?.isConfirmed),
        concatMap(({ result }) => {
          const options = {
            party_id: party._id,
            canceled_time: Date.now(),
            performRefund: result.checkboxStatus,
          };

          return this.markPartyService.setParty(options).pipe(
            // markParty refuses with HTTP 200 and success: false, e.g. when the refund fails;
            // the booking then stays active
            concatMap((response) => {
              if (response?.success) {
                return of(response);
              }
              // StripeService.refundPayment's own message: Stripe refused, or could not reach,
              // the refund. Anything else failed before or after it, so is not offered
              if (paid && options.performRefund && response?.error === 'Error refunding payment') {
                return this.offerCancelWithoutRefund(party, label, amount, response);
              }
              this.alertsService.addError(`The ${label} was not deleted.`);

              return of(response);
            }),
            catchError((err) => {
              this.alertsService.add({
                type: IAlertTypes.danger,
                heading: 'Error:',
                body: 'party not deleted successfully.',
              });

              return of(err);
            }),
          );
        }),
        catchError(err => of(err)),
      );
  }

  // Outside the cancellation window the delete dialog always refunds, so without this a booking
  // whose refund keeps failing (e.g. its Stripe account was disconnected) could never be canceled
  private offerCancelWithoutRefund(
    party: IParty,
    label: string,
    amount: string,
    refused: ApiResponseModelWrapper<IPartyOptions>,
  ): Observable<ApiResponseModelWrapper<IPartyOptions>> {
    return this.confirmationModalService.confirm({
      content: `The refund didn't go through, so this ${label} was not canceled. Cancel it without ` +
        `refunding the ${amount} payment? You can refund the guest later from your Stripe dashboard.`,
      closeBtnText: `Keep ${label}`,
      confirmBtnText: 'Cancel without refund',
      showCheckBox: false,
    })
      .pipe(
        take(1),
        concatMap(({ result }) => {
          if (!result?.isConfirmed) {
            return of(refused);
          }

          return this.markPartyService.setParty({
            party_id: party._id,
            canceled_time: Date.now(),
            performRefund: false,
          })
            .pipe(tap((response) => {
              if (!response?.success) {
                this.alertsService.addError(`The ${label} was not deleted.`);
              }
            }));
        }),
      );
  }

  // ADD & EDIT RESERVATION
  addEditReservation(
    user: AuthUser,
    selectedResource: Resource,
    selectedTimeForReservation: number,
    selectedEndTimeForReservation: number,
    reservationDateFilter: NgbDateStruct | string,
    floorPlanMode = false,
    floorplanResources?: FloorplanResources[],
    showSelectResourceMessage = false,
    showSuccessUpdateAlert = true,
    overrideWithResourceId?: string,
    party?: IParty,
    // TODO(IParty→Party): return flows through from saveParty() via modal dismiss; retype alongside.
  ): Observable<IParty | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party?.[partyNoteElement] &&
      typeof party[partyNoteElement] === 'string' &&
      Array.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0) {
      const partyNoteEl = party[partyNoteElement];
      party[partyNoteElement] = partyNoteEl.split(',');
    }

    if (!party) {
      const { usage } = user.location;

      // + 1 because we adding single party
      if (usage.parties + 1 > usage.partiesLimit) {
        const isLocationManager = user.locationUser && user.locationUser.role === UserLocationRole.manager;

        return this.partiesUsageService.partiesLimitReached(usage.partiesLimit, isLocationManager);
      }
    }

    // TODO(IParty→Party): second generic is the modal dismiss type = saveParty result; retype alongside.
    return this.modalController.create<AddEditPartyFloorplanComponent, IParty>(
      floorPlanMode ? AddEditPartyFloorplanComponent : AddEditPartyComponent,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        const componentProps: Partial<Record<keyof AddEditPartyFloorplanComponent, unknown>> = {};

        componentProps.parties = [];
        componentProps.user = {
          ...user.location,
          account: user.account,
        };
        componentProps.partyToEdit = party;
        componentProps.selectedResource = selectedResource;
        componentProps.userSchedule = this.schedule;
        componentProps.typeOfParty = PartyTypes.reservation;
        componentProps.selectedTime = selectedTimeForReservation;
        componentProps.selectedEndTime = selectedEndTimeForReservation;
        componentProps.showSelectResourceMessage = showSelectResourceMessage;
        componentProps.selectedDate = typeof reservationDateFilter === 'string'
          ? this.dateTimeService.momentToNgbDate(moment.tz(user.location.timezone))
          : reservationDateFilter;
        componentProps.availableOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);

        if (floorplanResources) {
          componentProps.floorplanResources = floorplanResources;
        }

        if (overrideWithResourceId && party) {
          modalElement.cssClass = 'transparent-ion-modal';
          componentProps.afterInit = (instance: ModalInstance): void => {
            this.afterInitLogic(instance, modalElement, party, overrideWithResourceId, selectedResource, showSuccessUpdateAlert);
          };
        } else if (overrideWithResourceId) {
          // New party with a table already chosen (floor plan "Add reservation" on a selected
          // table): keep the normal visible modal and just preselect the table. The transparent
          // afterInitLogic flow above assumes an existing party being moved — with no party it
          // would dereference party.size and leave the hidden modal blocking the page.
          componentProps.afterInit = (instance: ModalInstance): void => {
            const resource = this.resourcesService.getById(overrideWithResourceId);
            if (resource) {
              instance.addResourceToSelectedOnes(overrideWithResourceId, resource.services);
            }
          };
        }

        modalElement.componentProps = componentProps;
      },
    )
      .pipe(
        take(1),
        // TODO(IParty→Party): retype alongside saveParty() (updatedParty comes from modal dismiss).
        map((updatedParty: IParty) => {
          if (!updatedParty) {
            return updatedParty;
          }

          return {
            ...party,
            ...updatedParty,
          };
        }),
        catchError(err => of(err)),
      );
  }

  addEditReservationReactive({
    user,
    selectedTimeForReservation,
    selectedEndTimeForReservation,
    reservationDateFilter,
    party,
    // TODO(IParty→Party): return flows through from saveParty() via modal dismiss; retype alongside.
  }: AddEditPartyReactiveOptions): Observable<IParty | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party?.[partyNoteElement] &&
      typeof party[partyNoteElement] === 'string' &&
      Array.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0) {
      const partyNoteEl = party[partyNoteElement];
      party[partyNoteElement] = partyNoteEl.split(',');
    }

    if (!party) {
      const { usage } = user.location;

      // + 1 because we adding single party
      if (usage.parties + 1 > usage.partiesLimit) {
        const isLocationManager = user.locationUser && user.locationUser.role === UserLocationRole.manager;

        return this.partiesUsageService.partiesLimitReached(usage.partiesLimit, isLocationManager);
      }
    }

    // TODO(IParty→Party): second generic is the modal dismiss type = saveParty result; retype alongside.
    return this.modalController.create<AddEditPartyReactiveComponent, IParty>(
      AddEditPartyReactiveComponent,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        modalElement.componentProps = {
          parties: [],
          user: {
            ...user.location,
            account: user.account,
          },
          partyToEdit: party,
          userSchedule: this.schedule,
          selectedTime: selectedTimeForReservation,
          selectedEndTime: selectedEndTimeForReservation,
          selectedDate: typeof reservationDateFilter === 'string'
            ? this.dateTimeService.momentToNgbDate(moment.tz(user.location.timezone))
            : reservationDateFilter,
          availableOnBusinessPlan: this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly),
        };
      },
    )
      .pipe(
        take(1),
        // TODO(IParty→Party): retype alongside saveParty() (updatedParty comes from modal dismiss).
        map((updatedParty: IParty) => {
          if (!updatedParty) {
            return updatedParty;
          }

          return {
            ...party,
            ...updatedParty,
          };
        }),
        catchError(err => of(err)),
      );
  }

  chatParty(party: IParty, user: AuthUser): void {
    this.chatPartyService.open(party, user.location, user.account.features);
  }

  checkIn(party: IParty, loc: Location): Observable<unknown> {
    this.alertsService.clear();

    return this.markPartyService.setParty({
      party_id: party._id,
      checkin_time: Date.now(),
    })
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Checked In:',
            body: party.carrier
              ? `${party.truck} ${party.carrier}`
              : `${party.name} ${loc.displayOptions?.labels?.customer || 'customer'} of ${party.size}`,
          });
        }),
        catchError((err) => {
          this.alertsService.addError(`${loc.displayOptions?.labels?.customer || 'customer'} check-in failure.`);

          return of(err);
        }),
      );
  }

  completeParty(party: IParty, loc: Location, checkTracking = true): Observable<unknown> {
    const partyCantBeComplete = !party.checkin_time || !party.seated_time;
    const serverTrackingDisabled = checkTracking && !loc.bookingOptions.servingTracking;

    if (partyCantBeComplete || serverTrackingDisabled) {
      return of(null);
    }

    this.alertsService.clear();

    const options: IPartyOptions = {
      party_id: party._id,
      duration: Date.now() - party.seated_time,
      completedAt: Date.now(),
    };

    return this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Completed:',
            body: `${party.name} ${loc.displayOptions?.labels?.customer || 'customer'} of ${party.size}`,
          });
        }),
        catchError((err) => {
          this.alertsService.addError(`${loc.displayOptions?.labels?.customer || 'customer'} Served: failure.`);

          return of(err);
        }),
      );
  }

  private afterInitLogic(
    instance: ModalInstance,
    modalElement: HTMLIonModalElement,
    party: IParty,
    overrideWithResourceId: string,
    selectedResource: Resource,
    showSuccessUpdateAlert = true,
  ): void {
    instance.overrideIsWorkTime = true;
    instance.selectResources();
    instance.sizeChanged();
    instance.checkWorkTime();

    const resource = this.resourcesService.getById(overrideWithResourceId);
    if (!resource) {
      this.showModal(instance, modalElement);
      return;
    }

    const differentServices = party?.services?.length > 0 && !isEqual(resource.services.sort(), party.services.sort());
    if (differentServices) {
      instance.clearServicesResources();
    }

    instance.addResourceToSelectedOnes(overrideWithResourceId, resource.services);
    // A refused save shows the form whichever save callback is installed below: the zero-duration
    // one only re-saves.
    instance.revealForm = (): void => {
      this.showModal(instance, modalElement);
    };
    instance.abortHandOff = (): void => {
      void modalElement.dismiss(null);
    };
    instance.resourceOverrideCallback = (newParty: IParty): void => {
      const { location } = this.authRestaurantService.get();
      const actualSize = party.size;
      const effectiveSize = location.bookingOptions?.allowMultipleParties && !location.bookingOptions?.partySizeForTimeSlots ? 1 : actualSize;
      const extraConflicts = {
        amountOfServicesChanged: party.services.length > 0 && party.services.length !== newParty.services.length,
        wrongResourceSize: actualSize < selectedResource.capacity.min || effectiveSize > selectedResource.capacity.max,
        overlapping: instance.showOverlappingAlert,
        wrongPartySize: !!instance.resourceSizeAlert,
        blocked: instance.isReservation && Boolean(instance.blockedTime?.blocked) && !instance.overrideBlockedTime,
      };

      if (extraConflicts.blocked) {
        this.showModal(instance, modalElement);
        return;
      }

      if (differentServices) {
        instance.selectServices();
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.overlapping) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongResourceSize) {
        instance.showSizeAlert(selectedResource.capacity);
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongPartySize) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.amountOfServicesChanged) {
        instance.sizeChanged();
        instance.selectResources();
        this.showModal(instance, modalElement);
        return;
      }

      const conflicts = instance.doesEditingHaveAnyConflicts();
      let noConflicts = true;
      Object.entries(conflicts).forEach(([, state]) => {
        if (state) {
          noConflicts = false;
        }
      });

      if (!noConflicts) {
        this.showModal(instance, modalElement);
        return;
      }

      if (!newParty?.duration) {
        instance.selectServices();
        instance.resourceOverrideCallback = (): void => {
          this.closeModal(instance, showSuccessUpdateAlert);
        };
      } else {
        this.closeModal(instance);
      }
    };
  }

  private readonly closeModal = (instance: ModalInstance, showSuccessUpdateAlert = true): void => {
    instance.ok(showSuccessUpdateAlert);
  };

  private readonly showModal = (instance: ModalInstance, modal: HTMLIonModalElement): void => {
    instance.resourceOverrideCallback = undefined;
    instance.revealForm = undefined;
    instance.abortHandOff = undefined;
    modal.cssClass = '';
  };
}

interface AddEditPartyReactiveOptions {
  user: AuthUser;
  parties?: Party[];
  schedules?: Schedule[];
  showSelectResourceMessage: boolean;
  party?: IParty;
  reservationDateFilter: NgbDateStruct | string;
  selectedTimeForReservation: number;
  selectedEndTimeForReservation: number;
  selectedResource?: Resource;
}
